import { useCallback, useMemo, useState } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import CrossIcon from './cross-icon.svg'
import posthog from 'posthog-js'
import {
  applyVoucher,
  refreshCart,
  unapplyVoucher,
  setCouponCode,
  removeCouponCode,
} from '@booking/services/api'

import trackGA4 from '@booking/services/trackingGa4'
import { STORAGE_KEYS } from '@booking/state/booking.storage'
import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { CartLine } from '@booking/types/booking'

import styles from './index.module.scss'
import { handleAppError } from '../../lib/errors/handleAppError'
import { ApiError } from '../../lib/errors/apiError'

type DiscountBoxProps = {
  onApplyOrRemove?: () => void | Promise<void>
  hasFired?: boolean
}

export default function DiscountBox({
  onApplyOrRemove = () => {},
  hasFired = false,
}: DiscountBoxProps) {
  const t = useTranslations('booking')
  const state = useBookingState()
  const { dispatch, rebuildCart } = useBookingActionsCtx()

  const [open, setOpen] = useState(false)
  const [unapplying, setUnapplying] = useState(false)
  const [applying, setApplying] = useState(false)
  const [discount, setDiscount] = useState('')
  const [discountError, setDiscountError] = useState(false)

  const cart = state.cart

  const voucherLines = useMemo(
    () => (cart?.lines ?? []).filter((line) => Boolean(line?.voucher)),
    [cart?.lines],
  )

  const refreshCartWrapper = useCallback(
    async (cartId: string) => {
      if (!cartId) return null
      const refreshedCart = await refreshCart(cartId)
      dispatch({ type: 'SET_CART', cart: refreshedCart })
      return refreshedCart
    },
    [dispatch],
  )

  const addVoucher = async (cartId: string, code: string) => {
    try {
      const res = await applyVoucher(cartId, code)
      dispatch({ type: 'SET_CART', cart: res })
      const vouchers = (res?.lines ?? [])
        .filter((line) => Boolean(line?.voucher))
        .map((line) => line.voucher)
      setDiscount('')
      sessionStorage.setItem(STORAGE_KEYS.vouchers, JSON.stringify(vouchers))
      posthog.capture('discount_code_applied', {
        code_type: 'voucher',
        cart_id: cartId,
        vouchers_count: vouchers.length,
      })
    } catch (e) {
      if (e instanceof ApiError && e?.data?.errorCode === 'ENTITY_NOT_FOUND') {
        setDiscount('')
        setDiscountError(true)
        posthog.capture('discount_code_failed', {
          code_type: 'voucher',
          cart_id: cartId,
        })
      } else {
        handleAppError(e, { dispatch, t })
      }
    }
  }

  const addCoupon = async (cartId: string, code: string) => {
    const normalizedCode = code.toUpperCase()
    setDiscountError(false)
    try {
      const res = await setCouponCode(cartId, normalizedCode)
      dispatch({ type: 'SET_CART', cart: res })
      sessionStorage.setItem(STORAGE_KEYS.discount, normalizedCode)
      trackGA4.addDiscountCode(normalizedCode, true)
      posthog.capture('discount_code_applied', {
        code_type: 'coupon',
        cart_id: cartId,
        discount_percentage: res?.discount?.percentage ?? null,
      })
    } catch (e) {
      if (e instanceof ApiError && e?.data?.errorCode === 'ENTITY_NOT_FOUND') {
        setDiscount('')
        setDiscountError(true)
        posthog.capture('discount_code_failed', {
          code_type: 'coupon',
          cart_id: cartId,
        })
      } else {
        handleAppError(e, { dispatch, t })
      }
    }
  }

  const addDiscountCode = async () => {
    if (!cart) return
    const input = discount.trim()
    if (!input) return

    setApplying(true)
    let useCart = cart
    if (hasFired) {
      const rebuilt = await rebuildCart()
      if (rebuilt) useCart = rebuilt
    }
    const cartId = useCart.id
    if (!cartId) return

    await onApplyOrRemove()

    if (input.split('-').length === 5) {
      await addVoucher(cartId, input)
    } else {
      await addCoupon(cartId, input)
    }

    setApplying(false)
  }

  const unsetDiscountCode = async () => {
    if (!cart) return
    setUnapplying(true)
    let useCart = cart
    if (hasFired) {
      await onApplyOrRemove()
      const rebuilt = await rebuildCart()
      if (rebuilt) useCart = rebuilt
    } else {
      const cartId = useCart.id
      await onApplyOrRemove()
      const res = await removeCouponCode(cartId)
      dispatch({ type: 'SET_CART', cart: res })
    }
    setUnapplying(false)
    sessionStorage.removeItem(STORAGE_KEYS.discount)
  }

  const removeVoucher = async (line: CartLine) => {
    if (!cart) return
    const cartId = cart.id
    const voucherId = line.voucher.id
    if (!cartId || !voucherId) return
    await unapplyVoucher(cartId, voucherId)
    const refreshedCart = await refreshCartWrapper(cartId)
    const vouchers = (refreshedCart?.lines ?? [])
      .filter((nextLine) => Boolean(nextLine?.voucher))
      .map((nextLine) => nextLine.voucher)
    sessionStorage.setItem(STORAGE_KEYS.vouchers, JSON.stringify(vouchers))
  }

  if (!cart) return null

  const hasApplied = Boolean(cart.discount) || voucherLines.length > 0
  const showInput = !cart.discount && cart.total > 0

  return (
    <div className={styles.wrapper}>
      <div className={cn(styles.accordion, open && styles.accordionOpen)}>

        {/* ── Header row ── */}
        <button
          type="button"
          className={styles.header}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className={styles.tagIcon}>
            <TagSvg />
          </span>

          <span className={styles.title}>{t('ticket.discount.question')}</span>

          {!open && <span className={styles.saveMoney}>{t('ticket.discount.saveMoney')}</span>}

          <ChevronSvg open={open} />
        </button>

        {/* ── Applied codes (always visible when present) ── */}
        {hasApplied && (
          <div className={styles.appliedSection}>
            {voucherLines.map((line) => (
              <div key={line.voucher.id} className={cn(styles.applied, styles.voucher)}>
                <span>Voucher applied [{line.voucher.id.slice(0, 8)}]</span>
                <button
                  className={styles.remove}
                  type="button"
                  onClick={() => void removeVoucher(line)}
                >
                  <CrossIcon />
                </button>
              </div>
            ))}
            {cart.discount && (
              <div className={cn(styles.applied, unapplying && styles.blinker)}>
                <span>
                  {cart.discount.percentage}% {t('ticket.discount.applied')}
                </span>
                <button
                  className={styles.remove}
                  type="button"
                  onClick={() => void unsetDiscountCode()}
                  disabled={unapplying}
                >
                  <CrossIcon />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Collapsible input ── */}
        {open && showInput && (
          <>
            <div className={styles.divider} />
            <div className={styles.body}>
              <form
                className={styles.form}
                onSubmit={(e) => {
                  e.preventDefault()
                  void addDiscountCode()
                }}
              >
                <input
                  type="text"
                  placeholder={t('ticket.discount.enterCode')}
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className={styles.input}
                  autoFocus
                />
                <button
                  className={cn(styles.applyBtn, applying && styles.blinker)}
                  type="submit"
                  disabled={applying || !discount.trim()}
                >
                  {applying ? '...' : t('ticket.discount.add')}
                </button>
              </form>

              {discountError && (
                <p className={styles.discountError}>{t('ticket.discount.invalid')}</p>
              )}

              <p className={styles.note}>{t('ticket.discount.note')}</p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline SVG helpers

function TagSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8 8a2 2 0 0 0 2.828 0l7.172-7.172a2 2 0 0 0 0-2.828l-8-8z"
        stroke="var(--main-purple)"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="7.5" cy="7.5" r="1" fill="var(--main-purple)" />
    </svg>
  )
}

function ChevronSvg({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.chevron}
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="var(--main-purple)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
